/**
 * CinematicTypes — TypeScript interfaces for the cinematic step schema.
 * Each cinematic sequence is an array of discriminated-union steps
 * processed sequentially by CinematicDirector.
 */

// ── Base step (shared fields) ──

interface BaseStep {
  id: string;
  /** Pause before this step executes (ms) */
  delayBefore?: number;
  /** Pause after this step completes (ms) */
  delayAfter?: number;
  /** Condition name — if evaluates to true, skip this step. Resolved via ConditionRegistry. */
  skipIf?: string;
  /** When true, step is skipped during playback (kept in timeline for editing). */
  disabled?: boolean;
}

// ── Bubble template type ──

export type BubbleTemplate = 'trueBD' | 'speech' | 'thought' | 'shout' | 'whisper';

// ── Step variants ──

export interface DialogueStep extends BaseStep {
  type: 'dialogue';
  /** i18n key for speaker name, e.g. 'cinematic.speaker.operator' */
  speaker: string;
  /** i18n key for the line of dialogue */
  textKey: string;
  /** Bubble placement relative to screen */
  position?: 'top' | 'bottom' | 'center';
  /** Affects bubble border color: alert=red, calm=yellow, excited=green */
  emotion?: 'alert' | 'calm' | 'excited';
  /** Auto-dismiss after N ms (0 or undefined = wait for next step's delayBefore) */
  autoDismissMs?: number;
  /** Optional world-space anchor point for the bubble tail */
  worldAnchor?: { x: number; y: number };
  /** Texture key for character portrait (e.g. 'char_colossus'). Shown beside the bubble. */
  portrait?: string;
  /** Portrait display mode: inside bubble (small), or large at screen edge. Default: 'inBubble' */
  portraitDisplay?: 'inBubble' | 'bottomLeft' | 'bottomRight';
  /** Standing portrait size as % of screen height (0.2–0.8). Default: 0.4 */
  portraitScale?: number;
  /** Portrait offset {x,y} in pixels from default position */
  portraitOffset?: { x: number; y: number };
  /** How the portrait enters the screen. Default: 'slide' */
  portraitTransition?: 'slide' | 'fade' | 'pop' | 'none';
  /** Flip the portrait horizontally. Default: false */
  portraitFlip?: boolean;
  /** Show a smartphone frame as prop. Default: false */
  portraitFrame?: boolean;
  /** Smartphone frame scale as % of screen height (0.1–0.8). Default: 0.35 */
  frameScale?: number;
  /** Smartphone frame offset {x,y} relative to portrait */
  frameOffset?: { x: number; y: number };

  // ── BD bubble customization ──
  /** Bubble visual template (speech/thought/shout/whisper). Default: 'speech' */
  bubbleTemplate?: BubbleTemplate;
  /** Custom screen position {x,y} — overrides `position` enum if set */
  bubblePos?: { x: number; y: number };
  /** Tail tip screen-space position — overrides worldAnchor-derived tail if set */
  tailTip?: { x: number; y: number };
  /** Border/outline color hex (e.g. 0xff4444). Overrides emotion-derived color */
  borderColor?: number;
  /** Fill/background color hex. Default: TECH.BG */
  fillColor?: number;
  /** Text color hex. Default: 0xffffff */
  textColor?: number;

  // ── Optional spotlight highlight (combines dialogue + highlight like tutorial) ──
  /** Spotlight target — same format as SpotlightStep.target. When set, shows dim overlay + highlight rects */
  highlightTarget?: string | { x: number; y: number; w: number; h: number } | Array<{ x: number; y: number; w: number; h: number }>;
  /** true = target is in screen coords (UI), false = world coords. Default: false */
  highlightScreenSpace?: boolean;
  /** Dim overlay opacity (0–1). Default: 0.55 */
  highlightDimAlpha?: number;
  /** Highlight border color. Default: TECH.ACCENT (yellow) */
  highlightColor?: number;
  /** Pulsing animation on highlight. Default: true */
  highlightPulse?: boolean;
}

export interface CameraPanStep extends BaseStep {
  type: 'cameraPan';
  /** Named target or world coords */
  target: { x: number; y: number } | 'base' | 'spawn' | 'path';
  zoom?: number;
  /** Duration in ms (default from GOD.cinematicPanTime) */
  duration?: number;
}

export interface FakeClickStep extends BaseStep {
  type: 'fakeClick';
  /** Named target or absolute coords */
  target: { x: number; y: number } | 'placementTile' | 'startButton';
  /** true = target is in screen coords (UI element) */
  screenSpace?: boolean;
  /** How long to hold the click visual (ms) */
  holdMs?: number;
  /** If true, dispatch a real pointerdown/pointerup at target coords. Default: false */
  dispatch?: boolean;
}

export interface ActionStep extends BaseStep {
  type: 'action';
  action: 'spawnEnemies' | 'placeRobot' | 'startWave' | 'destroyEnemy' | 'selectPreset';
  params?: Record<string, unknown>;
}

export interface WaitStep extends BaseStep {
  type: 'wait';
  /** EventBus event name to wait for */
  waitEvent?: string;
  /** Max wait time before auto-advancing (ms) */
  timeoutMs?: number;
  /** When true, activates full combat (robots + enemies fight) without affecting score/progression.
   *  Auto-resolves on WAVE_COMPLETED or GAME_OVER. */
  simulateCombat?: boolean;
}

export interface RecapStep extends BaseStep {
  type: 'recap';
  /** i18n key for the recap title */
  titleKey: string;
  /** List of recap items (icon + i18n text) */
  items: Array<{ iconKey?: string; textKey: string }>;
  /** i18n key for the confirm button */
  confirmKey: string;
}

export interface SpotlightStep extends BaseStep {
  type: 'spotlight';
  /** Named target (single or multi), explicit rect, or array of rects */
  target: string | { x: number; y: number; w: number; h: number } | Array<{ x: number; y: number; w: number; h: number }>;
  /** true = screen coords (UI), false = world coords. Default: false */
  screenSpace?: boolean;
  /** Dim overlay opacity (0–1). Default: 0.55 */
  dimAlpha?: number;
  /** Highlight border color. Default: TECH.ACCENT */
  highlightColor?: number;
  /** Pulsing animation. Default: true */
  pulse?: boolean;
  /** Optional label i18n key shown near highlight */
  labelKey?: string;
  /** Label placement relative to highlight. Default: 'bottom' */
  labelPosition?: 'top' | 'bottom' | 'left' | 'right';
  /** Arrow direction pointing from label toward highlight centroid */
  arrowDir?: 'up' | 'down' | 'left' | 'right';
  /** How step advances. Default: 'click' */
  advanceOn?: 'click' | 'event';
  /** EventBus event name (when advanceOn='event') */
  waitEvent?: string;
  /** Timeout fallback (ms). Optional */
  timeoutMs?: number;
  /** Restrict input to highlighted area only. Default: true */
  restrictInput?: boolean;
}

// ── Union type ──

export type CinematicStep =
  | DialogueStep
  | CameraPanStep
  | FakeClickStep
  | ActionStep
  | WaitStep
  | RecapStep
  | SpotlightStep;

// ── Sequence trigger (embedded in sequence, configured via cinematic editor) ──

export interface SequenceTrigger {
  /** Scene-specific event name (e.g. 'workshopOpen', 'waveStart', 'levelTreeOpen') */
  event: string;
  /** Play only once per save slot (persisted in localStorage). Default: true */
  once?: boolean;
  /** For waveStart/waveEnd: which wave number (1-based). Omit = any wave. */
  waveNumber?: number;
  /** For tileEnter: which tile {col, row}. */
  tile?: { col: number; row: number };
}

/** Available trigger events per scene, used by editor UI */
export const SCENE_TRIGGER_EVENTS: Record<string, { event: string; label: string; hasWaveNumber?: boolean; hasTile?: boolean }[]> = {
  GameScene: [
    { event: 'mapStart', label: 'Map Start' },
    { event: 'waveStart', label: 'Wave Start', hasWaveNumber: true },
    { event: 'waveEnd', label: 'Wave End', hasWaveNumber: true },
    { event: 'victory', label: 'Victory' },
    { event: 'defeat', label: 'Defeat' },
    { event: 'tileEnter', label: 'Tile Enter', hasTile: true },
  ],
  WorkshopScene: [
    { event: 'workshopOpen', label: 'Workshop Open' },
    { event: 'designComplete', label: 'Design Complete' },
  ],
  LevelTreeScene: [
    { event: 'levelTreeOpen', label: 'Level Tree Open' },
    { event: 'levelSelected', label: 'Level Selected' },
  ],
  TitleScene: [
    { event: 'titleOpen', label: 'Title Open' },
  ],
};

// ── Sequence definition ──

export interface CinematicSequence {
  id: string;
  /** Lock SmartCamera during this sequence */
  lockCamera: boolean;
  /** Disable player pointer events during this sequence */
  lockInput: boolean;
  /** Which scene this sequence targets (e.g. 'GameScene', 'LevelTreeScene'). Metadata for editor. */
  targetScene?: string;
  /** Triggers — when should this sequence auto-play. Configured in cinematic editor. */
  triggers?: SequenceTrigger[];
  /** Runtime translations for custom dialogue text created in the editor (key→text). */
  customTranslations?: Record<string, string>;
  steps: CinematicStep[];
}
